import { PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { sectionBoundary } from "./sectionsCollapse";
import { isBlockCollapsible, isCollapsed, isSectionCollapsible } from "./utils";

import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/**
 * - `hidden`: how many hidden blocks
 * - `end`: the doc position just past the last hidden block
 */
export type CollapsedSectionInfo = { hidden: number; end: number };

export type CollapseDecorationState = {
  decorations: DecorationSet;
  sections: Map<number, CollapsedSectionInfo>;
};

export const collapseDecorationsPluginKey = new PluginKey<CollapseDecorationState>(
  "blockCollapseDecorations",
);

type HiddenBlockSpec = { hidden?: boolean; inHiddenSection?: boolean };

const IN_RUN_SPEC: HiddenBlockSpec = { hidden: true };

const IN_HIDDEN_SECTION_SPEC: HiddenBlockSpec = { hidden: true, inHiddenSection: true };

export function buildCollapseRunDecorations(doc: PMNode): CollapseDecorationState {
  const decorations: Decoration[] = [];
  const sections = new Map<number, CollapsedSectionInfo>();

  let head: { from: number; to: number } | null = null;
  let runLength = 0;

  const closeRun = () => {
    if (head) {
      decorations.push(
        Decoration.node(head.from, head.to, {
          style: `counter-increment: section ${runLength}`,
        }),
      );
    }
    head = null;
    runLength = 0;
  };

  let section: { node: PMNode; headPos: number; to: number; hidden: number; end: number } | null =
    null;

  const closeSection = () => {
    if (section) {
      sections.set(section.headPos, { hidden: section.hidden, end: section.end });

      if (section.hidden > 0) {
        decorations.push(
          Decoration.widget(section.to, sectionCounterWidget(section.hidden), {
            side: 1,
            key: `section-counter-${section.to}-${section.hidden}`,
          }),
        );
      }
    }
    section = null;
  };

  doc.forEach((node, offset) => {
    const nodeEnd = offset + node.nodeSize;

    if (section) {
      const boundary = sectionBoundary(section.node, node);

      if (boundary === "before") {
        closeSection();
      } else {
        decorations.push(
          Decoration.node(offset, nodeEnd, { class: "hidden" }, IN_HIDDEN_SECTION_SPEC),
        );
        section.hidden += 1;
        section.end = nodeEnd;

        if (boundary === "after") {
          closeSection();
        }
        return;
      }
    }

    if (isSectionCollapsible(node) && isCollapsed(node)) {
      closeRun();
      section = { node, headPos: offset, to: nodeEnd, hidden: 0, end: nodeEnd };
      return;
    }

    if (!(isBlockCollapsible(node) && isCollapsed(node))) {
      closeRun();
      return;
    }

    if (head) {
      decorations.push(Decoration.node(offset, nodeEnd, { class: "hidden" }, IN_RUN_SPEC));
    } else {
      head = { from: offset, to: nodeEnd };
    }

    runLength += 1;
  });

  closeRun();
  closeSection();

  return { decorations: DecorationSet.create(doc, decorations), sections };
}

export function collapsedSectionAbove(view: EditorView): number | null {
  const { state } = view;
  const { $head } = state.selection;
  const hidden = collapseDecorationsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;

  const depth = $head.depth - 1;

  if (depth < 0) {
    return null;
  }

  const parent = $head.node(depth);

  for (let index = $head.index(depth) - 1; index >= 0; index -= 1) {
    const pos = $head.posAtIndex(index, depth);
    const child = parent.child(index);
    const inHiddenSection = hidden
      .find(pos, pos + child.nodeSize)
      .some((deco) => deco.from === pos && (deco.spec as HiddenBlockSpec).inHiddenSection);

    if (inHiddenSection) {
      continue;
    }

    return isCollapsed(child) && isSectionCollapsible(child) ? pos : null;
  }

  return null;
}

export function isHiddenByCollapse(state: EditorState, pos: number): boolean {
  const node = state.doc.nodeAt(pos);
  const decorations = collapseDecorationsPluginKey.getState(state)?.decorations;

  if (!node || !decorations) {
    return false;
  }

  return decorations
    .find(pos, pos + node.nodeSize)
    .some((deco) => deco.from === pos && (deco.spec as HiddenBlockSpec).hidden);
}

function sectionCounterWidget(count: number): HTMLElement {
  const span = document.createElement("span");
  span.setAttribute("aria-hidden", "true");
  span.style.counterIncrement = `section ${count}`;
  return span;
}
